/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogStatus from 'layout/dialog/DialogStatus';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useTranslation } from 'react-i18next';
import Button from 'components/common/Button';

export default function MergeUserAccountDialog({
  dialog,
  toggleDialog,
  onSubmit,
  mergeAvailable,
  userToMergeId,
  error,
  mergeSucceeded,
  cleanTextField,
}) {
  const { t } = useTranslation();

  const getTitle = () => {
    if (mergeAvailable && !mergeSucceeded) {
      return (
        <>
          <DialogStatus variant="warning" />
          {t('user-management.merge.dialog.confirmation.title')}
        </>
      );
    }
    if (!mergeAvailable && !userToMergeId) {
      return (
        <>
          <DialogStatus variant="error" />
          {t('user-management.merge.dialog.notfound')}
        </>
      );
    }
    if (userToMergeId && error?.message === 'error.user.merge.lastAdmin') {
      return (
        <>
          <DialogStatus variant="error" />
          {t('user-management.merge.dialog.request.lastadmin.title')}
        </>
      );
    }
    if (mergeSucceeded) {
      return (
        <>
          <DialogStatus variant="success" />
          {t('user-management.merge.dialog.request.sent')}
        </>
      );
    }
    return null;
  };

  const getDescription = () => {
    if (mergeAvailable && !mergeSucceeded) {
      return t('user-management.merge.dialog.request.description');
    }
    if (!mergeAvailable && !userToMergeId) {
      return t('user-management.merge.dialog.request.notfound');
    }
    if (userToMergeId && error?.message === 'error.user.merge.lastAdmin') {
      return t('user-management.merge.dialog.request.lastadmin.error', {
        user: error?.params?.user,
        orgName: error?.params?.orgName,
      });
    }
    if (mergeSucceeded) {
      return t('user-management.merge.dialog.request.confirmation');
    }
    return null;
  };

  const getButtons = () => {
    if (mergeAvailable && !mergeSucceeded) {
      return (
        <>
          <Button
            data-testid="cancel-action"
            aria-label={t('actions.cancel')}
            color="default"
            onClick={() => toggleDialog(false)}
          >
            {t('actions.cancel')}
          </Button>
          <Button aria-label={t('actions.confirm')} data-testid="remove-user-btn" onClick={onSubmit} color="primary">
            {t('user-management.merge.dialog.action')}
          </Button>
        </>
      );
    }
    if ((!mergeAvailable && !userToMergeId) || (userToMergeId && error?.message === 'error.user.merge.lastAdmin')) {
      return (
        <>
          <Button
            aria-label={t('actions.close.and.search.again')}
            data-testid="merge-search-again"
            onClick={() => toggleDialog(false)}
            color="primary"
          >
            {t('actions.close.and.search.again')}
          </Button>
        </>
      );
    }
    if (mergeSucceeded) {
      return (
        <Button
          aria-label={t('user-management.merge.dialog.goback')}
          data-testid="merge-go-back"
          onClick={() => {
            toggleDialog(false);
            cleanTextField();
          }}
          color="primary"
        >
          {t('user-management.merge.dialog.goback')}
        </Button>
      );
    }
    return null;
  };

  return (
    <Dialog data-testid="merge-user-dialog" open={dialog.open} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h2">
          {getTitle()}
        </Typography>
      </DialogTitle>
      {getDescription() && <DialogContent>{getDescription()}</DialogContent>}
      <DialogActions>{getButtons()}</DialogActions>
    </Dialog>
  );
}
